import { loadScriptWithCallback } from "../plugins/script-loader/script-loader.js";
import { loadCss } from "../plugins/script-loader/styles-loader.js";

/**
 * XEditable - A class that dynamically initializes and configures XEditable fields (text or select).
 *
 * This class enables inline editing of text or select fields using the XEditable plugin.
 * It allows for dynamic script and CSS loading, flexible configuration, and customizable success handling.
 *
 * ##### PARAMS
 * @param {string} url - The URL to send the XEditable update request.
 * @param {function} onSuccess - Optional callback function executed on successful edit.
 */
class XEditable {
    /**
     * Constructor for the XEditable class.
     * @param {string} url - The URL to send the XEditable update request.
     * @param {function} onSuccess - Optional callback function executed on successful edit.
     */
    constructor(url, onSuccess = null) {
        if (!XEditable.instance) {
            this.scriptUrl = `${APPLICATION}/js/xeditable/js/bootstrap-editable.min.js`;
            this.cssUrl = `${APPLICATION}/js/xeditable/css/bootstrap-editable.css`;
            this.initialized = false;
            this.url = url;
            this.onSuccess = onSuccess || this.defaultSuccessHandler.bind(this);
            XEditable.instance = this;
        }

        return XEditable.instance;
    }

    /**
     * Adds XEditable functionality by loading required scripts and initializing fields.
     * @returns {Promise<void>}
     */
    async add() {
        await Promise.all([
            loadCss(this.cssUrl),
            new Promise((resolve) => {
                loadScriptWithCallback(this.scriptUrl, resolve);
            })
        ]);

        this.init();
    }

    /**
     * Initializes XEditable fields based on their type (text or select).
     */
    init() {
        const self = this;
        const elements = $(".xeditable");
        if (!this.initialized) {
            elements.each(function () {
                const $el = $(this);
                if (!$el.length || !document.body.contains($el[0])) return;

                const source = $el.data("source") || "";
                const type = $el.data("type") || "text";

                $el.editable({
                    source: source,
                    url: self.url,
                    type: type,
                    validate: function (value) {
                        if (value.trim() === "" && $el.attr("required")) {
                            return "This field is required";
                        }
                    },
                    params: function (params) {
                        const token = $el.attr("data-token");
                        if (token) {
                            params.token = JSON.parse(token);
                        }
                        return params;
                    },
                    success: self.onSuccess
                });

                if (type === "text") {
                    $el.on("hidden", function (e, reason) {
                        if (reason === "save" || reason === "nochange") {
                            const $next = $(this).closest("tr").next().find(".editable");
                            setTimeout(() => $next.editable("show"), 300);
                        }
                    });
                }

                if (type === "select") {
                    $el.on("save", function (e, params) {
                        if (params.newValue?.trim()) {
                            $(this).removeClass("text-gray-800");
                        } else {
                            $(this).addClass("text-gray-800");
                        }
                    });

                    if ($(".sectionPreviewTrigger").length > 0) {
                        $el.on("shown", function () {
                            $(this).closest("td").find(".sectionPreviewTrigger").parent().hide();
                        });

                        $el.on("hidden", function (e, reason) {
                            if (["save", "nochange", "onblur"].includes(reason)) {
                                $(this).closest("td").find(".sectionPreviewTrigger").parent().show();
                            }
                        });
                    }
                }
            });

            this.initialized = true;
        }
    }

    /**
     * Default success handler for editable fields.
     * @param {object} response - The response from the server.
     */
    defaultSuccessHandler(response) {
        if (response.code === 200) {
            showAlert(ALERT_TYPE, response);

            // Return an object with newValue for X-Editable to handle
            return {
                newValue: response.data.value
            };
        } else {
            showAlert(ALERT_TYPE, response);
            console.error("XEditable Error:", response);
            return response.message || "Error occurred";
        }
    }
}

export default XEditable;
